import * as React from 'react'
import { User } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Avatar — circular user image with initials and silhouette fallbacks, plus an
 * optional status dot. From the Figma Avatar specs.
 *
 *   size    xs | sm | md | lg | xl | 2xl   (24 / 32 / 40 / 48 / 56 / 64 px)
 *   src     image URL (rendered when present)
 *   name    used for the alt text and the initials fallback
 *   initials override the derived initials
 *   status  online | offline | busy        (green / red / yellow dot)
 *
 * Fallback order: image → initials (basil-100 surface, basil-700 text) →
 * a neutral user silhouette. The status dot carries a 1.5px white ring.
 */

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type AvatarStatus = 'online' | 'offline' | 'busy'

const sizeCls: Record<AvatarSize, string> = {
  xs: 'size-6 text-xs font-medium',
  sm: 'size-8 text-sm font-medium',
  md: 'size-10 text-base font-semibold',
  lg: 'size-12 text-lg font-semibold',
  xl: 'size-14 text-xl font-semibold',
  '2xl': 'size-16 text-2xl font-semibold',
}

const dotSizeCls: Record<AvatarSize, string> = {
  xs: 'size-1.5',
  sm: 'size-2',
  md: 'size-2.5',
  lg: 'size-3',
  xl: 'size-3.5',
  '2xl': 'size-4',
}

const dotColorCls: Record<AvatarStatus, string> = {
  online: 'bg-fbui-green-500',
  offline: 'bg-fbui-red-500',
  busy: 'bg-fbui-yellow-500',
}

/** First letters of up to two words, uppercased (e.g. "Wade Smith" → "WS"). */
function deriveInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

export interface AvatarProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'color'
> {
  size?: AvatarSize
  /** Image URL. When present, renders the photo. */
  src?: string
  /** Name — used for the alt text and the initials fallback. */
  name?: string
  /** Explicit initials, overriding those derived from `name`. */
  initials?: string
  /** Optional presence dot. */
  status?: AvatarStatus
}

export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, size = 'md', src, name, initials, status, ...props }, ref) => {
    const text = initials ?? (name ? deriveInitials(name) : '')
    return (
      <span
        ref={ref}
        className={cn('relative inline-flex shrink-0', className)}
        {...props}
      >
        <span
          className={cn(
            'flex items-center justify-center overflow-hidden rounded-full tracking-[-0.2px]',
            sizeCls[size],
            !src &&
              (text
                ? 'bg-fbui-basil-100 text-fbui-basil-700'
                : 'bg-fbui-gray-100 text-fbui-gray-400')
          )}
        >
          {src ? (
            <img
              src={src}
              alt={name ?? ''}
              className="size-full object-cover"
            />
          ) : text ? (
            <span>{text}</span>
          ) : (
            <User weight="fill" className="size-[60%]" aria-hidden="true" />
          )}
        </span>
        {status && (
          <span
            aria-label={status}
            className={cn(
              'absolute bottom-0 right-0 rounded-full ring-[1.5px] ring-fbui-base-white',
              dotSizeCls[size],
              dotColorCls[status]
            )}
          />
        )}
      </span>
    )
  }
)
Avatar.displayName = 'Avatar'
